import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { inventory } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { Money } from "@/components/shared/Money";
import { RevenueSourcePill } from "@/components/shared/Pill";

function isoMinusDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ServerDetail() {
  const { id } = useParams();
  const deviceId = Number(id);

  const device = useQuery({
    queryKey: ["inventory", "device", deviceId],
    queryFn: () => inventory.get(deviceId),
    enabled: Number.isFinite(deviceId),
  });

  const today = new Date().toISOString().slice(0, 10);
  const start = useMemo(() => isoMinusDays(90), []);

  const series = useQuery({
    queryKey: ["inventory", "revenue", deviceId, start, today],
    queryFn: () => inventory.revenue(deviceId, start, today),
    enabled: Number.isFinite(deviceId),
  });

  if (device.isLoading) return <Spinner label="Loading device…" />;
  if (device.error || !device.data)
    return <div className="text-red-700">{(device.error as Error)?.message ?? "not found"}</div>;

  const d = device.data;
  const data = (series.data ?? []).map((p) => ({
    date: p.date,
    vast: Number(p.vast),
    contract: Number(p.contract),
    total: Number(p.total),
  }));

  return (
    <div className="space-y-6">
      <header className="flex items-baseline gap-4">
        <h1 className="text-xl font-semibold">{d.name}</h1>
        <div className="text-sm text-slate-500">
          {d.gpu_type || d.device_type} · {d.site}
        </div>
        <RevenueSourcePill source={d.revenue_source} />
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="MTD revenue">
          <Money value={d.mtd_revenue} />
        </Stat>
        <Stat label="Trailing 30d">
          <Money value={d.trailing_30d_revenue} />
        </Stat>
        <Stat label="Tenant">{d.tenant ?? <span className="text-slate-400">—</span>}</Stat>
        <Stat label="Contract end">
          {d.contract_end_date ?? <span className="text-slate-400">—</span>}
        </Stat>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800 p-4">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-medium">Revenue (last 90 days)</h2>
          <div className="text-xs text-slate-500">vast + contract, daily</div>
        </div>
        <div className="h-64">
          {series.isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tickFormatter={(t) => t.slice(5)} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="vast"
                  stackId="1"
                  stroke="#2563eb"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                />
                <Area
                  type="monotone"
                  dataKey="contract"
                  stackId="1"
                  stroke="#059669"
                  fill="#10b981"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800 p-4 text-sm">
        <h2 className="font-medium mb-2">Identifiers</h2>
        <dl className="grid grid-cols-2 gap-y-1 gap-x-4 text-sm">
          <dt className="text-slate-500">Asset tag</dt>
          <dd>{d.asset_tag || "—"}</dd>
          <dt className="text-slate-500">Vast machine ID</dt>
          <dd>{d.vast_machine_id ?? "—"}</dd>
          <dt className="text-slate-500">Tempo deployment</dt>
          <dd>{d.tempo_deployment_xref || "—"}</dd>
          <dt className="text-slate-500">Status</dt>
          <dd>{d.status || "—"}</dd>
        </dl>
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 dark:bg-slate-900 dark:border-slate-800">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{children}</div>
    </div>
  );
}
